import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Modal, PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";

import { SerpOverlayContent } from "./SerpOverlayContent";
import { SerpOverlayHeaderInfo } from "./SerpOverlayHeaderInfo";
import { SerpOverlayPageSwitcher } from "./SerpOverlayPageSwitcher";
import { useSerpOverlayContext } from "./SerpOverlayProvider";
import { SerpOverlayModalBody, SerpOverlayModalContent, SerpOverlaySubHeader } from "./styles";
import { buildSerpOverlayPageTabs, toSerpOverlayLocationDevice } from "./utils";

export const SerpOverlay = () => {
    const { translate } = useTranslate();
    const {
        isOpen,
        serpParams,
        snapshotData,
        isLoading,
        hasError,
        activePageIndex,
        iframeErrorsByPageIndex,
        closeSerpOverlay,
        handlePageTabClick,
        handleIframeError,
    } = useSerpOverlayContext();

    const { params } = useCurrentStateAndParams<RTRawParams>();
    const device = toSerpOverlayLocationDevice(serpParams?.device ?? params.device);

    const activeRecord = snapshotData?.records?.[activePageIndex];
    const recordsCount = snapshotData?.records?.length ?? 0;
    const pageTabs = useMemo(() => buildSerpOverlayPageTabs(recordsCount, translate), [recordsCount, translate]);
    const showPageTabs = !isLoading && !hasError && recordsCount > 0;

    return (
        <Modal
            data-automation="serp-overlay"
            isOpen={isOpen}
            isFullScreen
            shouldCloseOnOverlayClick={false}
            onRequestClose={closeSerpOverlay}
        >
            <SerpOverlayModalBody>
                <Modal.Header withBorder>
                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Modal.Title>
                            <Stack
                                gap="4px"
                                alignSelf="flex-start"
                            >
                                {serpParams?.keyword}
                                {serpParams && device ? (
                                    <SerpOverlayHeaderInfo
                                        date={activeRecord?.date ?? serpParams.serpLinkDate}
                                        geoLocationText={serpParams.geoLocation.text}
                                        device={device}
                                    />
                                ) : null}
                            </Stack>
                        </Modal.Title>
                        <Modal.CloseButton
                            data-automation="serp-overlay-close"
                            onClick={closeSerpOverlay}
                        />
                    </Flex>
                </Modal.Header>
                {showPageTabs ? (
                    <SerpOverlaySubHeader data-automation="serp-overlay-page-tabs-container">
                        <SerpOverlayPageSwitcher
                            items={pageTabs}
                            selectedValue={String(activePageIndex)}
                            onItemClick={handlePageTabClick}
                        />
                    </SerpOverlaySubHeader>
                ) : null}
                <Modal.Content
                    flex={1}
                    minHeight={0}
                    height="100%"
                    padding="0"
                >
                    <SerpOverlayModalContent
                        flexDirection="column"
                        width="100%"
                        height="100%"
                    >
                        {isLoading ? (
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                width="100%"
                                height="100%"
                                data-automation="serp-overlay-loading"
                            >
                                <PixelPlaceholderLoader
                                    width="100%"
                                    height="100%"
                                />
                            </Flex>
                        ) : (
                            <SerpOverlayContent
                                records={snapshotData?.records ?? []}
                                activePageIndex={activePageIndex}
                                hasError={hasError}
                                iframeErrorsByPageIndex={iframeErrorsByPageIndex}
                                onIframeError={handleIframeError}
                            />
                        )}
                    </SerpOverlayModalContent>
                </Modal.Content>
            </SerpOverlayModalBody>
        </Modal>
    );
};
